/** @jsxImportSource @emotion/react */
import React, { ComponentProps, useCallback, useMemo, useRef } from "react";
import { jsx, css } from "@emotion/react";
import DropdownMenu, { DropdownItem, DropdownItemGroup } from "@atlaskit/dropdown-menu";
import Textarea from "@atlaskit/textarea";
import { useIntl } from "components/i18n";
import Button from "@atlaskit/button";
import MoreIcon from "@atlaskit/icon/glyph/more";
import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import { FormattedMessage } from "react-intl-next";

type Props = ComponentProps<typeof Textarea> & {}

const VariablesDropdown = ({variables, insertText}:any) => {

  const el = useRef(null);
  return (
    <DropdownMenu
      appearance="default"
      placement="bottom"
      trigger={({ triggerRef, onClick, ...props }) => (
        <div ref={el}>
          <Button
            {...props}
            onClick={(e) => {
              if (onClick) {
                onClick(e);
              }
              // @ts-ignore
              el.current.firstChild.focus()
            }}
            iconAfter={<ChevronDownIcon label="expand" />}
            appearance="subtle"
            ref={triggerRef}
          ><FormattedMessage id="common.variables" defaultMessage="Variables"/></Button></div>)}
    >
      {variables.map((group:any) =>
        <DropdownItemGroup key={group.title} title={group.title}>
          {group.items.map((item: { value: string | number | null | undefined; label: React.ReactNode; }) =>
            <DropdownItem key={item.value} onClick={() => insertText(item.value)}>{item.label}</DropdownItem>
          )}
        </DropdownItemGroup>
      )}
    </DropdownMenu>)
}


const TextAreaWithVariables: React.FC<Props> = (props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { onChange, value } = props;

  const intl = useIntl();

  const variables = useMemo(() => [
    {
      title: intl.formatMessage({ id: "common.invoice", defaultMessage: "Invoice" }),
      items: [
        {
          label: intl.formatMessage({ id: "common.invoiceNumber", defaultMessage: "Invoice number" }),
          value: "{{factuur.factuurnummer}}"
          // value: '{{invoice.invoiceNumber}}'
        },
        {
          label: intl.formatMessage({ id: "common.invoiceDate", defaultMessage: "Invoice date" }),
          value: "{{datum factuur.factuurdatum}}"
          // value: '{{date invoice.invoiceDate}}'
        },
        {
          label: intl.formatMessage({ id: "common.dueDate", defaultMessage: "Due date" }),
          value: "{{datum factuur.vervaldatum}}"
          // value: '{{date invoice.dueDate}}'
        },
        {
          label: intl.formatMessage({ id: "common.paymentTerm", defaultMessage: "Payment term" }),
          value: "{{factuur.betalingstermijn}}"
          // value: '{{invoice.paymentTerm}}'
        },
        {
          label: intl.formatMessage({ id: "common.totalAmount", defaultMessage: "Total amount" }),
          value: "{{bedrag factuur.totaalbedrag}}"
          // value: '{{amount invoice.totalAmount}}'
        }
      ]
    },
    {
      title: intl.formatMessage({ id: "common.date", defaultMessage: "Date" }),
      items: [
        {
          label: intl.formatMessage({ id: "common.day", defaultMessage: "Day" }),
          value: "{{factuur.dag}}"
          // value: '{{invoice.day}}',
        },
        {
          label: intl.formatMessage({ id: "common.week", defaultMessage: "Week" }),
          value: "{{factuur.week}}"
          // value: '{{invoice.week}}',
        },
        {
          label: intl.formatMessage({ id: "common.dayOfMonth", defaultMessage: "Day of month" }),
          value: "{{invoice.dagVanDeMaand}}"
          // value: '{{invoice.dayOfMonth}}',
        },
        {
          label: intl.formatMessage({ id: "common.month", defaultMessage: "Month" }),
          value: "{{factuur.maand}}"
        },
        {
          label: intl.formatMessage({ id: "common.lastMonth", defaultMessage: "Last month" }),
          value: "{{datum factuur.factuurdatum min=\"1 maand\" formaat=\"MMMM\"}}"
        },
        {
          label: intl.formatMessage({ id: "common.nextMonth", defaultMessage: "Next month" }),
          value: "{{datum factuur.factuurdatum plus=\"1 maand\" formaat=\"MMMM\"}}"
        },
        {
          label: intl.formatMessage({ id: "common.startOfMonth", defaultMessage: "Start of month" }),
          value: "{{datum factuur.factuurdatum begin=\"maand\"}}"
        },
        {
          label: intl.formatMessage({ id: "common.endOfMonth", defaultMessage: "End of month" }),
          value: "{{datum factuur.factuurdatum eind=\"maand\"}}"
        },
        {
          label: intl.formatMessage({ id: "common.quarter", defaultMessage: "Quarter" }),
          value: "{{factuur.kwartaal}}"
        },
        {
          label: intl.formatMessage({ id: "common.year", defaultMessage: "Year" }),
          value: "{{factuur.jaar}}"
        }
      ]
    },
    {
      title: intl.formatMessage({ id: "common.customer", defaultMessage: "Customer" }),
      items: [
        {
          label: intl.formatMessage({ id: "common.customerName", defaultMessage: "Customer name" }),
          value: "{{klant.naam}}"
          // value: '{{customer.name}}'
        },
        {
          label: intl.formatMessage({ id: "common.contactPerson", defaultMessage: "Contact person" }),
          value: "{{klant.contactpersoon}}"
          // value: '{{customer.contactName}}'
        },
        {
          label: intl.formatMessage({ id: "common.firstName", defaultMessage: "First name" }),
          value: "{{klant.voornaam}}"
          // value: '{{customer.contactFirstName}}'
        },
        {
          label: intl.formatMessage({ id: "common.lastName", defaultMessage: "Last name" }),
          value: "{{klant.achternaam}}"
          // value: '{{customer.contactLastName}}'
        }
      ]
    }
  ],[]);


  const insertText = useCallback((textToInsert: string) => {
    const textArea = textAreaRef.current;
    if (!textArea) return;
    const scrollPos = textArea.scrollTop;
    let caretPos = textArea.selectionStart;

    const textBeforeCursorPosition = (textArea.value).substring(0, caretPos);
    const textAfterCursorPosition = (textArea.value).substring(textArea.selectionEnd, textArea.value.length);
    textArea.value = textBeforeCursorPosition + textToInsert + textAfterCursorPosition;
    // @ts-ignore
    onChange(textArea.value);

    caretPos += textToInsert.length;
    textArea.selectionStart = caretPos;
    textArea.selectionEnd = caretPos;
    textArea.focus();
    textArea.scrollTop = scrollPos;
  }, [value, textAreaRef.current]);

  // @ts-ignore
  const { rows } = props;
  const variablesPadding = rows === 1 ? "2px" : "0.5rem";

  return (
      <div css={css`position: relative;

    &:focus-within, &:hover {
      #variables-dropdown {
        opacity: 1
      }
    }

    &:focus-within {
      #variables-dropdown {
        background: white;
      }
    }

    Textarea {
      padding-right: 8rem;
    }
  `}
  >
    <Textarea {...props} ref={textAreaRef} />
    <div id="variables-dropdown" css={css`position: absolute;
      top: ${variablesPadding};
      right: ${variablesPadding};
      opacity: 0;`}>
    <VariablesDropdown insertText={insertText} variables={variables}/>
    </div>
  </div>
      );
};

export default TextAreaWithVariables;
